


























































import { mixins } from "vue-class-component";
import { Component, Inject, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";

import FormErrors from "@/lib/components/FormErrors.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import IdGenerator from "@/lib/services/id-generator";
import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import dataStore from "@/store";

import FactoryRushCapacityProductGroup from "../../factory-rush-capacity-product-group.model";
import FactoryRushCapacitySlot from "../../factory-rush-capacity-slot.model";
import FactoryRushCapacityType from "../../factory-rush-capacity-type.model";
import FactoryRushCapacityStore from "../../store";

@Component({
  components: {
    FormErrors,
    SubmitButton,
  },
})
export default class FactoryRushCapacityEditForm extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly factoryId!: string;

  @Prop({ required: true })
  public readonly groups!: FactoryRushCapacityProductGroup[];

  @Prop({ required: true })
  public readonly configuredSlots!: FactoryRushCapacitySlot[];

  @Prop({ required: true })
  public readonly types!: FactoryRushCapacityType[];

  @Inject("IdGenerator")
  private fIdGenerator!: IdGenerator;

  private fCellValues: Record<string, string> = {};
  private fFactoryRushCapacityStore: FactoryRushCapacityStore;

  get columnWidth(): string {
    const columnsCount = this.types.length ? this.types.length : 1;
    const value = Math.round((80 * 100) / columnsCount) / 100;

    return value.toString() + "%";
  }

  get hasMatrixData(): boolean {
    return this.visibleGroups.length > 0 && this.types.length > 0;
  }

  get visibleGroups(): FactoryRushCapacityProductGroup[] {
    return this.groups.filter((group) =>
      this.fFactoryRushCapacityStore.hasFactoryRushCapacityProductGroupMembers(
        group.id
      )
    );
  }

  constructor() {
    super();

    this.fFactoryRushCapacityStore = getModule(
      FactoryRushCapacityStore,
      dataStore
    );
  }

  public getCellValue(groupId: string, typeId: string): string {
    return this.fCellValues[this.getCellKey(groupId, typeId)] || "";
  }

  public getInputClasses(
    groupId: string,
    typeId: string
  ): Record<string, boolean> {
    const value = this.getCellValue(groupId, typeId);

    return {
      "-not-empty": value.trim().length > 0,
    };
  }

  public onCellInput(groupId: string, typeId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.$set(this.fCellValues, this.getCellKey(groupId, typeId), target.value);
  }

  protected async performSubmit(): Promise<void> {
    if (!this.hasMatrixData) {
      throw new Error(
        "Rush capacity groups or types are not available. Reload the page and try again."
      );
    }

    const slots: FactoryRushCapacitySlot[] = [];

    this.groups.forEach((group) => {
      this.types.forEach((type) => {
        const rawValue = this.getCellValue(group.id, type.id).trim();

        if (!rawValue) {
          return;
        }

        if (!/^\d+$/.test(rawValue) || +rawValue < 1) {
          throw new Error(
            "Capacity values must be positive integers. Leave a cell blank to clear it."
          );
        }

        const existingSlot = this.getConfiguredSlot(group.id, type.id);

        const slot = new FactoryRushCapacitySlot(
          existingSlot ? existingSlot.id : this.fIdGenerator.getId(),
          this.factoryId,
          group.id,
          type.id,
          +rawValue
        );

        slot.isNew = existingSlot == null;

        slots.push(slot);
      });
    });

    await this.fFactoryRushCapacityStore.saveFactoryRushCapacitySlotsByFactoryId(
      {
        factoryId: this.factoryId,
        slots,
      }
    );

    this.syncCellValues();
  }

  private getCellKey(groupId: string, typeId: string): string {
    return `${groupId}:${typeId}`;
  }

  private getConfiguredSlot(
    groupId: string,
    typeId: string
  ): FactoryRushCapacitySlot | undefined {
    return this.configuredSlots.find(
      (slot) => slot.productGroup === groupId && slot.type === typeId
    );
  }

  private syncCellValues(
    configuredSlots: FactoryRushCapacitySlot[] = this.configuredSlots
  ): void {
    const values: Record<string, string> = {};

    configuredSlots.forEach((slot) => {
      values[
        this.getCellKey(slot.productGroup, slot.type)
      ] = slot.capacity.toString();
    });

    this.fCellValues = values;
  }

  @Watch("configuredSlots", { immediate: true })
  private _onConfiguredSlotsChange() {
    this.syncCellValues();
  }
}
